import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { TERM_DOCUMENT_ACCEPT } from "@/services/generate-term/generate-term";
import {
  ReplaceSignedTermFormValues,
  replaceSignedTermSchema,
} from "@/services/validation-schemas/replace-signed-term";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { REPLACE_SIGNED_TERM_LABELS } from "../../utils/messages";

export interface ReplaceSignedTermSubmission extends ReplaceSignedTermFormValues {
  createUser: boolean;
}

interface ReplaceSignedTermModalProps {
  onConfirm: (submission: ReplaceSignedTermSubmission) => Promise<void>;
}

const FILE_INPUT_ID = "replace-signed-term-file";
const REASON_INPUT_ID = "replace-signed-term-reason";

const ReplaceSignedTermModal = ({
  onConfirm,
}: ReplaceSignedTermModalProps): JSX.Element => {
  const { handleModal } = useDialogModal();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const form = useForm<ReplaceSignedTermFormValues>({
    resolver: zodResolver(replaceSignedTermSchema),
    mode: "onChange",
    defaultValues: { comment: "" },
  });
  const { errors, isValid } = form.formState;

  const canSubmit: boolean = isValid && !isSubmitting;

  const submit = (createUser: boolean) =>
    form.handleSubmit(async (values: ReplaceSignedTermFormValues): Promise<void> => {
      setIsSubmitting(true);
      try {
        await onConfirm({ ...values, createUser });
      } finally {
        setIsSubmitting(false);
      }
    });

  const close = (): void => {
    handleModal({ isOpen: false });
  };

  return (
    <form className="flex flex-col gap-5" onSubmit={(event) => event.preventDefault()}>
      <p className="text-sm text-muted-foreground">
        {REPLACE_SIGNED_TERM_LABELS.HINT}
      </p>

      <div className="space-y-2">
        <Label htmlFor={FILE_INPUT_ID}>{REPLACE_SIGNED_TERM_LABELS.FILE}</Label>
        <Controller
          control={form.control}
          name="file"
          render={({ field }) => (
            <Input
              id={FILE_INPUT_ID}
              type="file"
              accept={TERM_DOCUMENT_ACCEPT}
              onChange={(event) => field.onChange(event.target.files?.[0])}
              onBlur={field.onBlur}
              disabled={isSubmitting}
            />
          )}
        />
        {errors.file && (
          <p className="text-sm text-destructive">{errors.file.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={REASON_INPUT_ID}>{REPLACE_SIGNED_TERM_LABELS.REASON}</Label>
        <Textarea
          id={REASON_INPUT_ID}
          placeholder={REPLACE_SIGNED_TERM_LABELS.REASON_PLACEHOLDER}
          disabled={isSubmitting}
          rows={3}
          {...form.register("comment")}
        />
        {errors.comment && (
          <p className="text-sm text-destructive">{errors.comment.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          className="w-full font-medium text-green-700 border-green-200 hover:bg-green-50"
          disabled={!canSubmit}
          onClick={submit(false)}
        >
          {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {REPLACE_SIGNED_TERM_LABELS.CONFIRM_WITHOUT_USER}
        </Button>
        <Button
          type="button"
          className="w-full font-medium bg-green-500 hover:bg-green-600 text-white"
          disabled={!canSubmit}
          onClick={submit(true)}
        >
          {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {REPLACE_SIGNED_TERM_LABELS.CONFIRM_WITH_USER}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={close}
          disabled={isSubmitting}
        >
          {REPLACE_SIGNED_TERM_LABELS.BACK}
        </Button>
      </div>
    </form>
  );
};

export default ReplaceSignedTermModal;
